import type { OmitStrict } from '@nx/stdlib';

import type { FullFilterRule, GetRuleByFilterRuleId, InitialFilterRule } from '../../state/filter/types';
import type {
  CategoryPropertyKey,
  CategoryWithStyle,
  GeneralPropertyKey,
  RelationshipTypeWithStyle,
} from '../../types/perspective';

export const APPLIED = 'applied';
export const APPLY_TYPE = 'applyType';
export const TYPE = 'type';
export const CATEGORY = 'category';
export const RELATIONSHIP = 'relationship';
export const ENTRY_ID = 'entryId';
export const BASED_ON = 'basedOn';
export const BOOL_VALUES = 'boolValues';
export const CONDITION = 'condition';
export const CONDITION_VALUE = 'conditionValue';
export const CONDITION_VALUE_RANGE = 'conditionValueRange';
export const STRING_VALUES = 'stringValues';
export const IS_TIME_ZONE_CONVERT_ENABLED = 'isTimeZoneConvertEnabled';
export const SELECTED_TIME_ZONE = 'selectedTimeZone';

const DROPDOWN_WRAPPER_KEY = 'dropdown-wrapper-key';
const ENTER_KEY_CODE = 13;

const boolValuesInitialState: FullFilterRule['boolValues'] = [];
const conditionInitialState = null;
const conditionValuesInitialState = null;
export const stringValuesInitialState: FullFilterRule['stringValues'] = [];

export const defaultFilterRule: OmitStrict<InitialFilterRule, 'id'> = {
  [APPLIED]: false,
  [APPLY_TYPE]: false,
};

export const defaultPropertyBulkUpdate = {
  [BOOL_VALUES]: boolValuesInitialState,
  [CONDITION]: conditionInitialState,
  [CONDITION_VALUE]: conditionValuesInitialState,
  [CONDITION_VALUE_RANGE]: conditionValuesInitialState,
  [STRING_VALUES]: stringValuesInitialState,
  [IS_TIME_ZONE_CONVERT_ENABLED]: false,
  [SELECTED_TIME_ZONE]: 'Z',
};

const appliedDataTypes = [
  'string',
  'boolean',
  'bool',
  'number',
  'bigint',
  'date',
  'localdatetime',
  'localtime',
  'time',
  'datetime',
];

const _getCategoryById = (categories: CategoryWithStyle[], categoryId: number): CategoryWithStyle | undefined =>
  categories.find(({ id }) => id === categoryId);

const _getRelationshipTypeById = (
  relationshipTypes: RelationshipTypeWithStyle[],
  relationshipId: string,
): RelationshipTypeWithStyle | undefined => relationshipTypes.find(({ id }) => id === relationshipId);

export const isClickOutsideOnNoSelectedValue = (event: Event) => {
  if ('keyCode' in event && event.keyCode === ENTER_KEY_CODE) return false;
  return (
    event.target instanceof HTMLElement &&
    event.target.classList &&
    event.target.classList.contains(DROPDOWN_WRAPPER_KEY)
  );
};

export const filterAppliedDataTypes = (property: CategoryPropertyKey) =>
  appliedDataTypes.includes(property.dataType.toLowerCase());

export const getBasedOnValueFromProperty = (property: CategoryPropertyKey | GeneralPropertyKey | undefined) => {
  if (!property) return '';
  const name = 'name' in property ? property.name : undefined;
  const propertyKey = 'propertyKey' in property ? property.propertyKey : undefined;
  return `${name ?? propertyKey}_${property.dataType}`;
};

export const getNameFromProperty = (property: CategoryPropertyKey | GeneralPropertyKey) => {
  if ('name' in property) return property.name;
  return property.propertyKey;
};

export const getIdFromEntry = (entry: { id?: string; name?: string }) => entry.id ?? (entry.name as string);

export const assembleUniqueId = (
  property: CategoryPropertyKey | GeneralPropertyKey | undefined,
  filterRuleId: string,
  value: string,
) => `${filterRuleId}_${getBasedOnValueFromProperty(property)}__${value}`;

type FullEntry =
  | (RelationshipTypeWithStyle & { type: typeof CATEGORY })
  | (CategoryWithStyle & { type: typeof RELATIONSHIP });
export const getStoredFullEntry = ({
  filterRuleId,
  getRuleByFilterRuleId,
  categories,
  relationshipTypes,
}: {
  filterRuleId: string;
  getRuleByFilterRuleId: GetRuleByFilterRuleId;
  categories: CategoryWithStyle[];
  relationshipTypes: RelationshipTypeWithStyle[];
}): FullEntry | null => {
  const ruleContent = getRuleByFilterRuleId(filterRuleId);
  if (!ruleContent) return null;
  const type = 'type' in ruleContent ? ruleContent.type : undefined;
  const entryId = 'entryId' in ruleContent ? ruleContent.entryId : undefined;
  if ((type !== CATEGORY && type !== RELATIONSHIP) || entryId === undefined) return null;
  const entry: CategoryWithStyle | RelationshipTypeWithStyle | undefined =
    type === CATEGORY
      ? _getCategoryById(categories, entryId as number)
      : _getRelationshipTypeById(relationshipTypes, entryId as string);
  if (!entry) return null;
  return {
    ...entry,
    type,
  } as FullEntry;
};

export const getStoredProperty = (props: {
  filterRuleId: string;
  getRuleByFilterRuleId: GetRuleByFilterRuleId;
  categories: CategoryWithStyle[];
  relationshipTypes: RelationshipTypeWithStyle[];
}) => {
  const entry = getStoredFullEntry({ ...props });
  if (!entry || !entry.properties) return null;
  const basedOnValue = getStoredBasedOnValue({ ...props });
  return entry.properties.find((prop) => basedOnValue === getBasedOnValueFromProperty(prop));
};

export const getStoredAppliedValue = ({
  filterRuleId,
  getRuleByFilterRuleId,
}: {
  filterRuleId: string;
  getRuleByFilterRuleId: GetRuleByFilterRuleId;
}) => {
  const ruleContent = getRuleByFilterRuleId(filterRuleId);
  return ruleContent?.applied ?? false;
};

export const getStoredApplyTypeValue = ({
  filterRuleId,
  getRuleByFilterRuleId,
}: {
  filterRuleId: string;
  getRuleByFilterRuleId: GetRuleByFilterRuleId;
}) => {
  const ruleContent = getRuleByFilterRuleId(filterRuleId);
  return ruleContent?.applyType ?? false;
};

const getStoredBasedOnValue = ({
  filterRuleId,
  getRuleByFilterRuleId,
}: {
  filterRuleId: string;
  getRuleByFilterRuleId: GetRuleByFilterRuleId;
}) => {
  const ruleContent = getRuleByFilterRuleId(filterRuleId);
  return ruleContent && 'basedOn' in ruleContent ? ruleContent.basedOn : '';
};

export const getStoredBoolValues = ({
  filterRuleId,
  getRuleByFilterRuleId,
}: {
  filterRuleId: string;
  getRuleByFilterRuleId: GetRuleByFilterRuleId;
}) => {
  const ruleContent = getRuleByFilterRuleId(filterRuleId);
  return ruleContent && 'boolValues' in ruleContent ? ruleContent.boolValues : boolValuesInitialState;
};

export const getStoredStringValues = ({
  getRuleByFilterRuleId,
  filterRuleId,
}: {
  filterRuleId: string;
  getRuleByFilterRuleId: GetRuleByFilterRuleId;
}) => {
  const ruleContent = getRuleByFilterRuleId(filterRuleId);
  return ruleContent && 'stringValues' in ruleContent ? ruleContent.stringValues : stringValuesInitialState;
};
